import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  input,
  LOCALE_ID,
  output,
  Signal,
  viewChild,
} from '@angular/core';
import { DecimalPipe, formatNumber } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { MenuItem } from '../../../data/models';
import { FallbackImage } from '../../../shared/fallback-image';
import { HighlightRange, splitByRanges } from '../../../shared/search-text';
import { FALLBACK_IMAGE } from '../../../core/constants';
import { countKey, fromFollowsPrice } from '../../../core/i18n';
import { LanguageService } from '../../../core/language.service';
import { ToastService } from '../../../shared/toast/toast.service';
import { itemPriceView } from '../item-pricing';
import { PromoStore } from '../promo-store';
import { Layout } from '../menu.types';
import { CartStore } from '../cart-store';

@Component({
  selector: 'app-menu-item-card',
  imports: [DecimalPipe, FallbackImage, TranslatePipe],
  template: `
    <article
      class="qm-card group flex flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm transition-all duration-500 hover:shadow-xl"
      [class.qm-tile]="!isList()"
    >
      <button
        type="button"
        class="qm-card-photo relative block w-full overflow-hidden transition-all focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white focus-visible:inset-ring-4 focus-visible:inset-ring-slate-900"
        [class.cursor-zoom-in]="zoomable()"
        [class.cursor-default]="!zoomable()"
        [class.h-56]="isList()"
        [class.h-40]="!isList()"
        [disabled]="!zoomable()"
        [attr.aria-label]="
          zoomable() ? ('menu.openPhoto' | translate: { name: item().name }) : null
        "
        (click)="openImage.emit(thumb)"
      >
        <img
          #thumb
          [src]="item().imageUrl || fallbackSrc"
          [alt]="item().name"
          loading="lazy"
          appFallbackImage
          class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div
          class="qm-photo-scrim absolute inset-0 bg-linear-to-t from-black/40 to-transparent opacity-60"
          aria-hidden="true"
        ></div>
        @if (promoBadge(); as badge) {
          <span class="qm-promo" aria-hidden="true">{{ badge }}</span>
        }
        <div
          class="qm-price absolute right-3 bottom-3 rounded-lg bg-white/90 px-2 py-1 shadow-lg backdrop-blur-sm"
          [class.origin-right]="!isList()"
          [class.scale-90]="!isList()"
        >
          @if (priceView().oldPrice; as old) {
            <s class="qm-price-old" aria-hidden="true">{{ old | number: '1.0-0' }}</s>
          }
          <span class="text-sm font-bold text-slate-900">
            @if (hasVariants() && !fromAfter()) {
              <span class="text-xs font-semibold text-slate-500">{{
                'common.from' | translate
              }}</span>
            }
            {{ priceView().price | number: '1.0-0' }} {{ 'common.currency' | translate }}
            @if (hasVariants() && fromAfter()) {
              <span class="text-xs font-semibold text-slate-500">{{
                'common.from' | translate
              }}</span>
            }
          </span>
        </div>
      </button>

      <div class="flex grow flex-col" [class.p-3]="!isList()" [class.p-5]="isList()">
        <h3
          class="qm-name mb-1 leading-tight font-bold text-slate-900"
          [class.text-xl]="isList()"
          [class.text-sm]="!isList()"
        >
          @for (part of nameSegments(); track $index) {
            @if (part.hit) {
              <mark>{{ part.text }}</mark>
            } @else {
              <span>{{ part.text }}</span>
            }
          }
        </h3>

        @if (promoAria(); as label) {
          <p class="sr-only">{{ label }}</p>
        }

        @if (isList()) {
          <p class="qm-desc line-clamp-2 text-sm leading-relaxed text-slate-500">
            @for (part of descriptionSegments(); track $index) {
              @if (part.hit) {
                <mark>{{ part.text }}</mark>
              } @else {
                <span>{{ part.text }}</span>
              }
            }
          </p>
        }

        @if (ordering() && hasDetails()) {
          <!-- Позиция с вариантами/добавками не продаётся «как есть» — «+» открывает шторку выбора. -->
          @if (detailsEnabled()) {
            <div class="mt-3 pt-1" [class.mt-auto]="isList()">
              <button
                #chooseBtn
                type="button"
                (click)="openDetails.emit(chooseBtn)"
                [attr.aria-label]="('menu.choose' | translate) + ': ' + item().name"
                class="qm-add flex min-h-10 w-full items-center justify-center gap-1.5 rounded-full text-sm font-semibold shadow-sm transition-transform [text-shadow:0_1px_2px_rgba(0,0,0,0.35)] active:scale-[0.97] focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 focus-visible:ring-offset-white focus-visible:outline-none motion-reduce:transition-none motion-reduce:active:scale-100"
                style="background: var(--qm-action); color: var(--qm-action-fg)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                  class="shrink-0"
                >
                  <path d="M12 5v14"></path>
                  <path d="M5 12h14"></path>
                </svg>
                @if (isList()) {
                  <span>{{ 'menu.choose' | translate }}</span>
                }
              </button>
            </div>
          }
        } @else if (ordering()) {
          <div class="mt-3 pt-1" [class.mt-auto]="isList()">
            @if (qty() === 0) {
              <button
                #addBtn
                type="button"
                (click)="add()"
                [attr.aria-label]="('menu.add' | translate) + ': ' + item().name"
                class="qm-add flex min-h-10 w-full items-center justify-center gap-1.5 rounded-full text-sm font-semibold shadow-sm transition-transform [text-shadow:0_1px_2px_rgba(0,0,0,0.35)] active:scale-[0.97] focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 focus-visible:ring-offset-white focus-visible:outline-none motion-reduce:transition-none motion-reduce:active:scale-100"
                style="background: var(--qm-action); color: var(--qm-action-fg)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                  class="shrink-0"
                >
                  <path d="M12 5v14"></path>
                  <path d="M5 12h14"></path>
                </svg>
                @if (isList()) {
                  <span>{{ 'menu.add' | translate }}</span>
                }
              </button>
            } @else {
              <div
                class="qm-stepper flex min-h-10 items-center justify-between rounded-full p-1"
                style="background: var(--qm-action-soft)"
              >
                <button
                  type="button"
                  (click)="dec()"
                  [attr.aria-label]="'menu.removeOne' | translate: { name: item().name }"
                  class="qm-step qm-step--minus grid h-9 w-9 place-items-center rounded-full bg-white text-slate-700 shadow-sm transition-transform active:scale-90 focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none motion-reduce:active:scale-100"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5 12h14"></path>
                  </svg>
                </button>
                <span
                  class="qm-qty min-w-8 text-center text-sm font-bold text-slate-900 tabular-nums"
                  aria-live="polite"
                >
                  {{ qty() }}
                </span>
                <button
                  #plusBtn
                  type="button"
                  (click)="add()"
                  [attr.aria-label]="'menu.addOne' | translate: { name: item().name }"
                  class="qm-step qm-step--plus grid h-9 w-9 place-items-center rounded-full shadow-sm transition-transform [text-shadow:0_1px_2px_rgba(0,0,0,0.35)] active:scale-90 focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none motion-reduce:active:scale-100"
                  style="background: var(--qm-action); color: var(--qm-action-fg)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 5v14"></path>
                    <path d="M5 12h14"></path>
                  </svg>
                </button>
              </div>
            }
          </div>
        }

        @if (!ordering() && hasDetails() && detailsEnabled()) {
          <button
            #details
            type="button"
            class="qm-details mt-auto flex min-h-6 cursor-pointer items-center gap-1 pt-2 text-[10px] font-bold tracking-widest uppercase focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:outline-none"
            style="color: var(--qm-accent-ink)"
            [attr.aria-label]="'menu.detailsAria' | translate: { name: item().name }"
            (click)="openDetails.emit(details)"
          >
            {{ detailsLabel() }}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        }
      </div>
    </article>
  `,
  styles: `
    :host {
      display: contents;
    }

    @keyframes card-fade-in {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    article {
      animation: card-fade-in 0.4s ease-out forwards;
      content-visibility: auto;
      /* Оценка до первого рендера (auto запоминает реальную высоту после): список ~24rem
         (картинка h-56 + текст), плитка ~20rem — ближе к правде, меньше прыжок скроллбара. */
      contain-intrinsic-size: auto 24rem;
    }

    article.qm-tile {
      contain-intrinsic-size: auto 20rem;
    }

    .qm-promo {
      position: absolute;
      top: 0.75rem;
      right: 0.75rem;
      padding: 0.125rem 0.5rem;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 700;
      line-height: 1.25rem;
      background: var(--qm-accent);
      color: var(--qm-accent-fg);
    }

    .qm-price-old {
      display: block;
      font-size: 0.6875rem;
      font-weight: 500;
      line-height: 1;
      text-align: end;
      color: var(--qm-ink);
    }

    /* Text is forced to slate-900: the description's slate-500 would dip below WCAG AA
       on the tinted background. */
    mark {
      background-color: color-mix(
        in srgb,
        var(--brand-fill, var(--brand-primary, #0f172a)) 15%,
        transparent
      );
      color: #0f172a;
      border-radius: 0.25em;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuItemCard {
  item = input.required<MenuItem>();
  layout = input.required<Layout>();
  /** When true, show the add-to-cart quantity control. */
  ordering = input(false);
  /** Search-match spans in the name/description to highlight; empty while browsing. */
  nameRanges = input<readonly HighlightRange[]>([]);
  descriptionRanges = input<readonly HighlightRange[]>([]);
  /** Details affordance appears only once the deferred item sheet is loaded. */
  detailsEnabled = input(false);
  /** Emits the thumbnail element so the parent can hand it to the image viewer. */
  openImage = output<HTMLImageElement>();
  /** Emits the trigger element so the item sheet can restore focus on close. */
  openDetails = output<HTMLElement>();

  private cart = inject(CartStore);
  private toast = inject(ToastService);
  private injector = inject(Injector);
  private language = inject(LanguageService);
  private promo = inject(PromoStore);
  private locale = inject(LOCALE_ID);
  private addBtn = viewChild<ElementRef<HTMLButtonElement>>('addBtn');
  private plusBtn = viewChild<ElementRef<HTMLButtonElement>>('plusBtn');
  private fallbackImg = viewChild(FallbackImage);

  protected readonly fallbackSrc = FALLBACK_IMAGE;
  protected readonly isList = computed(() => this.layout() === 'list');
  protected readonly qty = computed(() => this.cart.qtyOf(this.item().id));
  // `?? ''` — null name/description can slip through the typed model.
  protected readonly nameSegments = computed(() =>
    splitByRanges(this.item().name ?? '', this.nameRanges()),
  );
  protected readonly descriptionSegments = computed(() =>
    splitByRanges(this.item().description ?? '', this.descriptionRanges()),
  );
  /** No src => nothing to zoom; a load failure (placeholder shown) also counts as "no image". */
  protected readonly zoomable = computed(
    () => !!this.item().imageUrl?.trim() && !(this.fallbackImg()?.failed() ?? false),
  );

  protected add(): void {
    const wasEmpty = this.qty() === 0;
    // The card is the only place a 31st unique line can appear, so the cap is announced here;
    // nothing changed, so the add button keeps focus.
    if (!this.cart.add(this.item())) {
      this.toast.show(this.language.t('cart.max30'), 'info');
      return;
    }
    // Crossing 0→1 swaps the add button for the stepper, destroying the focused
    // node; keep keyboard focus alive by moving it to the stepper's "+".
    if (wasEmpty) this.focusAfterRender(this.plusBtn);
  }

  protected dec(): void {
    const willEmpty = this.qty() === 1;
    this.cart.decrement(this.item().id);
    if (willEmpty) this.focusAfterRender(this.addBtn);
  }

  private focusAfterRender(ref: Signal<ElementRef<HTMLButtonElement> | undefined>): void {
    afterNextRender(() => ref()?.nativeElement.focus(), { injector: this.injector });
  }

  protected readonly hasVariants = computed(() => this.item().variants.length > 0);
  protected readonly fromAfter = computed(() => fromFollowsPrice(this.language.lang()));
  protected readonly hasDetails = computed(
    () => this.hasVariants() || this.item().modificationGroups.length > 0,
  );

  protected readonly priceView = computed(() => itemPriceView(this.item(), this.promo.promos()));
  protected readonly promoBadge = computed(() => {
    const view = this.priceView();
    return view.percent > 0 ? this.promo.badge(view.percent, view.upTo) : '';
  });
  /** Плашка цены внутри кнопки фото с aria-label — её текст скринридер не прочтёт. */
  protected readonly promoAria = computed(() => {
    const badge = this.promoBadge();
    if (!badge) return '';
    let price = `${formatNumber(this.priceView().price, this.locale, '1.0-0')} ${this.language.t('common.currency')}`;
    if (this.hasVariants()) {
      const from = this.language.t('common.from');
      price = this.fromAfter() ? `${price} ${from}` : `${from} ${price}`;
    }
    return this.language.t('menu.promoAria', { badge, price });
  });

  protected readonly detailsLabel = computed(() => {
    const item = this.item();
    const lang = this.language.lang();
    const parts: string[] = [];
    const variants = item.variants.length;
    if (variants > 0) {
      parts.push(`${variants} ${this.language.t(countKey('menu.variants', variants, lang))}`);
    }
    if (item.modificationGroups.length > 0) parts.push(this.language.t('menu.addons'));
    return parts.join(' · ');
  });
}
